import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Btn, Select, Empty, Field, Input } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/Logo";
import {
  FileText,
  FileSpreadsheet,
  Eye,
  Pencil,
  Trash2,
  IndianRupee,
} from "lucide-react";
import { toast } from "sonner";

const WORKFLOW_STATUSES = [
  "draft",
  "raised",
  "submitted",
  "cancelled",
];

const SYSTEM_PAYMENT_STATUSES =
  new Set([
    "partially_paid",
    "paid",
    "overdue",
  ]);

const workflowStatus = (invoice) => {
  if (
    SYSTEM_PAYMENT_STATUSES.has(
      invoice.status
    )
  ) {
    return (
      invoice.pre_payment_status
      || "raised"
    );
  }

  return (
    invoice.status
    || "draft"
  );
};

const paymentStatusLabel = (invoice) => {
  const received =
    Number(
      invoice.paid_amount || 0
    );

  const pending =
    Number(
      invoice.balance_due
      ??
      invoice.pending_amount
      ??
      invoice.grand_total
      ??
      0
    );

  if (
    invoice.status ===
    "cancelled"
  ) {
    return "Cancelled";
  }

  if (
    invoice.status ===
    "draft"
  ) {
    return "Not Raised";
  }

  if (pending <= 0) {
    return "Paid";
  }

  if (
    invoice.status ===
    "overdue"
  ) {
    return "Overdue";
  }

  if (received > 0) {
    return "Partially Paid";
  }

  return "Unpaid";
};

const paymentStatusClass = (label) => {
  switch (label) {
    case "Paid":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";

    case "Partially Paid":
      return "bg-amber-50 text-amber-700 border-amber-200";

    case "Overdue":
      return "bg-rose-50 text-rose-700 border-rose-200";

    case "Unpaid":
      return "bg-orange-50 text-orange-700 border-orange-200";

    case "Not Raised":
      return "bg-slate-50 text-slate-600 border-slate-200";

    default:
      return "bg-slate-50 text-slate-600 border-slate-200";
  }
};

const PAYMENT_MODES = [
  ["bank_transfer", "Bank Transfer"],
  ["upi", "UPI"],
  ["cheque", "Cheque"],
  ["cash", "Cash"],
  ["neft", "NEFT"],
  ["rtgs", "RTGS"],
  ["imps", "IMPS"],
  ["other", "Other"],
];


const inr = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function Invoices() {
  const [searchParams] = useSearchParams();

  const queryMonth =
    searchParams.get("month") || "";

  const queryClient =
    searchParams.get("client_id") || "";

  const queryStatus =
    searchParams.get("status") || "";

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [preview, setPreview] = useState(null);
  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [payment, setPayment] =
    useState(null);

  const [paymentForm, setPaymentForm] =
    useState({
      amount: "",
      receipt_date:
        new Date()
          .toISOString()
          .slice(0, 10),
      payment_mode:
        "bank_transfer",
      reference_no: "",
      notes: "",
    });

  const load = () =>
    api.get("/invoices").then((r) => setInvoices(r.data));

  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const cname = (id) =>
    clients.find((c) => c.id === id)?.name || "—";

  const visibleInvoices = invoices.filter((inv) => {
    if (
      queryMonth &&
      inv.month !== queryMonth
    ) {
      return false;
    }

    if (
      queryClient &&
      queryClient !== "all" &&
      inv.client_id !== queryClient
    ) {
      return false;
    }

    if (queryStatus === "pending") {
      return [
        "draft",
        "raised",
        "submitted",
        "partially_paid",
        "overdue",
      ].includes(inv.status);
    }

    if (
      queryStatus &&
      queryStatus !== "pending"
    ) {
      return inv.status === queryStatus;
    }

    return true;
  });

  const setStatus = async (id, status) => {
    try {
      await api.put(`/invoices/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openPreview = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setPreview(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openPayment = async (inv) => {
    try {
      if (
        [
          "draft",
          "cancelled",
          "paid",
        ].includes(inv.status)
      ) {
        toast.error(
          inv.status === "paid"
            ? "This invoice is already fully paid"
            : "Raise the invoice before recording payment"
        );
        return;
      }

      const { data } =
        await api.get(
          `/invoices/${inv.id}`
        );

      setPayment(data);

      setPaymentForm({
        amount: "",
        receipt_date:
          new Date()
            .toISOString()
            .slice(0, 10),
        payment_mode:
          "bank_transfer",
        reference_no: "",
        notes: "",
      });

    } catch (e) {
      toast.error(
        errMsg(e)
      );
    }
  };


  const savePayment = async () => {
    if (!payment?.invoice?.id) {
      return;
    }

    const amount =
      Number(
        paymentForm.amount || 0
      );

    const balance =
      Number(
        payment
          ?.payment_summary
          ?.pending_amount ||
        payment
          ?.payment_summary
          ?.balance_due ||
        0
      );

    if (
      !Number.isFinite(amount)
      || amount <= 0
    ) {
      toast.error(
        "Enter a valid received amount"
      );
      return;
    }

    if (
      amount >
      balance + 0.001
    ) {
      toast.error(
        `Amount cannot exceed pending balance of ₹${inr(balance)}`
      );
      return;
    }

    try {
      await api.post(
        `/clients/${payment.invoice.client_id}/receipts`,
        {
          invoice_id:
            payment.invoice.id,

          amount:
            amount,

          receipt_date:
            paymentForm
              .receipt_date,

          payment_mode:
            paymentForm
              .payment_mode,

          reference_no:
            paymentForm
              .reference_no,

          notes:
            paymentForm.notes,
        }
      );

      toast.success(
        "Payment recorded successfully"
      );

      const invoiceId =
        payment.invoice.id;

      setPayment(null);

      load();

      if (
        preview?.invoice?.id
        === invoiceId
      ) {
        const { data } =
          await api.get(
            `/invoices/${invoiceId}`
          );

        setPreview(data);
      }

    } catch (e) {
      toast.error(
        errMsg(e)
      );
    }
  };


  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      const inv = data.invoice;

      setEdit(data);

      setEditForm({
        invoice_date:
          inv.invoice_date ||
          (inv.created_at || "").slice(0, 10),

        due_date:
          inv.due_date || "",

        work_order:
          inv.work_order || "",

        reference_no:
          inv.reference_no || "",

        remarks:
          inv.remarks || "",

        billing_address:
          inv.billing_address ||
          data.client?.address ||
          "",

        subject:
          inv.subject || "",

        reverse_charge:
          inv.reverse_charge || "NO",

        payment_terms_days:
          inv.payment_terms_days ?? 30,

        gst_pct:
          inv.gst_pct ?? "18",
      });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveEdit = async () => {
    if (!edit?.invoice?.id) return;

    try {
      await api.put(
        `/invoices/${edit.invoice.id}`,
        editForm
      );

      toast.success("Invoice updated");
      setEdit(null);
      setEditForm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteInvoice = async (inv) => {
    if (inv.status !== "draft") {
      toast.error("Only Draft invoices can be deleted");
      return;
    }

    const ok = window.confirm(
      `Delete draft invoice ${inv.number}?`
    );

    if (!ok) return;

    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Draft invoice deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle={
          `Client billing generated from finalized payroll${
            queryMonth ? ` · ${queryMonth}` : ""
          }`
        }
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {visibleInvoices.length ? (
            <table
              className="nk-table"
              data-testid="invoices-table"
            >
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Client</th>
                  <th>Month</th>
                  <th>Payroll Cost</th>
                  <th>Service Charge</th>
                  <th>Taxable Value</th>
                  <th>GST</th>
                  <th>Grand Total</th>
                  <th>Received</th>
                  <th>Pending Amount</th>
                  <th>Invoice Status</th>
                  <th>Payment Status</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {visibleInvoices.map((i) => (
                  <tr
                    key={i.id}
                    data-testid={`invoice-row-${i.id}`}
                  >
                    <td className="font-semibold">
                      {i.number}
                    </td>

                    <td>{cname(i.client_id)}</td>

                    <td className="nk-num">
                      {i.month}
                    </td>

                    <td className="nk-num">
                      ₹{inr(
                        i.payroll_cost ??
                        (
                          Number(i.subtotal || 0)
                          - Number(i.margin_amount || 0)
                        )
                      )}
                    </td>

                    <td className="nk-num">
                      ₹{inr(i.service_charge ?? i.margin_amount)}
                    </td>

                    <td className="nk-num">
                      ₹{inr(
                        i.taxable_value ??
                        i.subtotal
                      )}
                    </td>

                    <td className="nk-num">
                      ₹{inr(i.gst)}
                    </td>

                    <td className="nk-num font-bold">
                      ₹{inr(i.grand_total)}
                    </td>

                    <td className="nk-num text-emerald-700 font-semibold">
                      ₹{inr(
                        i.paid_amount
                        ?? 0
                      )}
                    </td>

                    <td className="nk-num text-rose-700 font-bold">
                      ₹{inr(
                        i.balance_due
                        ??
                        i.pending_amount
                        ??
                        i.grand_total
                      )}
                    </td>

                    <td>
                      <Select
                        value={
                          workflowStatus(i)
                        }
                        onChange={(e) =>
                          setStatus(
                            i.id,
                            e.target.value
                          )
                        }
                        className="!py-1 !text-xs w-32 capitalize"
                        data-testid={`invoice-status-${i.id}`}
                      >
                        {WORKFLOW_STATUSES.map(
                          (s) => (
                            <option
                              key={s}
                              value={s}
                            >
                              {s.replace(
                                /_/g,
                                " "
                              )}
                            </option>
                          )
                        )}
                      </Select>
                    </td>

                    <td>
                      {(() => {
                        const label =
                          paymentStatusLabel(i);

                        return (
                          <span
                            className={
                              `inline-flex items-center px-2 py-1 rounded-full border text-[10px] font-semibold whitespace-nowrap ${paymentStatusClass(label)}`
                            }
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </td>

                    <td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Btn
                          variant="ghost"
                          onClick={() =>
                            openPreview(i.id)
                          }
                          title="View"
                          data-testid={`invoice-preview-${i.id}`}
                        >
                          <Eye size={14} />
                        </Btn>

                        {i.status === "draft" && (
                          <Btn
                            variant="ghost"
                            onClick={() =>
                              openEdit(i.id)
                            }
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </Btn>
                        )}

                        {i.status === "draft" && (
                          <Btn
                            variant="ghost"
                            onClick={() =>
                              deleteInvoice(i)
                            }
                            title="Delete Draft"
                          >
                            <Trash2 size={14} />
                          </Btn>
                        )}

                        {![
                          "draft",
                          "cancelled",
                          "paid",
                        ].includes(i.status) &&
                        Number(
                          i.balance_due
                          ??
                          i.pending_amount
                          ??
                          i.grand_total
                          ??
                          0
                        ) > 0 && (
                          <Btn
                            variant="ghost"
                            onClick={() =>
                              openPayment(i)
                            }
                            title="Record Payment"
                            data-testid={`invoice-payment-${i.id}`}
                          >
                            <IndianRupee
                              size={14}
                            />
                          </Btn>
                        )}

                        <Btn
                          variant="ghost"
                          onClick={() =>
                            downloadFile(
                              `/invoices/${i.id}/xlsx`,
                              `${i.number.replace(/\//g, "_")}.xlsx`
                            )
                          }
                          title="Excel"
                        >
                          <FileSpreadsheet size={14} />
                        </Btn>

                        <Btn
                          variant="outline"
                          onClick={() =>
                            downloadFile(
                              `/invoices/${i.id}/pdf`,
                              `${i.number.replace(/\//g, "_")}.pdf`
                            )
                          }
                          title="PDF"
                        >
                          <FileText size={14} />
                          PDF
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty
              title="No invoices yet"
              hint="Generate an invoice from a calculated payroll run."
            />
          )}
        </div>
      </Card>

      {/* PREVIEW */}
      <Dialog
        open={!!preview}
        onOpenChange={(o) => !o && setPreview(null)}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>
                Invoice Preview
              </span>

              {preview?.invoice?.status && (
                <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold capitalize">
                  Present Status:{" "}
                  {preview.invoice.status.replace(
                    /_/g,
                    " "
                  )}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {preview?.payment_summary && (
            <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden">

              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm text-slate-900">
                    Payment & Balance
                  </div>

                  <div className="text-xs text-slate-500 mt-0.5">
                    Collection status for this invoice
                  </div>
                </div>

                {![
                  "draft",
                  "cancelled",
                  "paid",
                ].includes(
                  preview.invoice.status
                ) &&
                Number(
                  preview
                    .payment_summary
                    .pending_amount
                  || 0
                ) > 0 && (
                  <Btn
                    onClick={() =>
                      openPayment(
                        preview.invoice
                      )
                    }
                  >
                    <IndianRupee
                      size={14}
                    />
                    Record Payment
                  </Btn>
                )}
              </div>


              <div className="p-4">

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

                  <SummaryBox
                    label="Grand Total"
                    value={
                      preview
                        .payment_summary
                        .grand_total
                    }
                  />

                  <SummaryBox
                    label="Amount Received"
                    value={
                      preview
                        .payment_summary
                        .received_amount
                    }
                  />

                  <SummaryBox
                    label="Pending Amount"
                    value={
                      preview
                        .payment_summary
                        .pending_amount
                    }
                    strong
                  />

                  <div className="border border-slate-200 rounded-md p-3 bg-white">
                    <div className="nk-overline">
                      Present Status
                    </div>

                    <div className="mt-1 font-bold text-slate-900 capitalize">
                      {(
                        preview
                          .payment_summary
                          .status
                        || "—"
                      ).replace(
                        /_/g,
                        " "
                      )}
                    </div>

                    {preview
                      .payment_summary
                      .last_payment_date && (
                      <div className="text-xs text-slate-500 mt-1">
                        Last payment:{" "}
                        {
                          preview
                            .payment_summary
                            .last_payment_date
                        }
                      </div>
                    )}
                  </div>

                </div>


                {!!preview.receipts?.length && (
                  <div className="mt-4 overflow-x-auto">

                    <div className="font-semibold text-xs text-slate-700 mb-2">
                      Payment History
                    </div>

                    <table className="nk-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Mode</th>
                          <th>Reference</th>
                          <th>Amount</th>
                          <th>Notes</th>
                        </tr>
                      </thead>

                      <tbody>
                        {preview.receipts.map(
                          (r) => (
                            <tr key={r.id}>
                              <td>
                                {r.receipt_date}
                              </td>

                              <td className="capitalize">
                                {(
                                  r.payment_mode
                                  || ""
                                ).replace(
                                  /_/g,
                                  " "
                                )}
                              </td>

                              <td>
                                {r.reference_no || "—"}
                              </td>

                              <td className="nk-num font-semibold text-emerald-700">
                                ₹{inr(
                                  r.amount
                                )}
                              </td>

                              <td>
                                {r.notes || "—"}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>

                  </div>
                )}

              </div>
            </div>
          )}


          {preview && (
            <InvoicePreview
              {...preview}
              inr={inr}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* RECORD PAYMENT */}

      <Dialog
        open={!!payment}
        onOpenChange={(o) => {
          if (!o) {
            setPayment(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">

          <DialogHeader>
            <DialogTitle>
              Record Invoice Payment
            </DialogTitle>
          </DialogHeader>


          {payment && (
            <div>

              <div className="grid grid-cols-3 gap-3 mb-5">

                <SummaryBox
                  label="Grand Total"
                  value={
                    payment
                      .payment_summary
                      ?.grand_total
                  }
                />

                <SummaryBox
                  label="Received"
                  value={
                    payment
                      .payment_summary
                      ?.received_amount
                  }
                />

                <SummaryBox
                  label="Pending"
                  value={
                    payment
                      .payment_summary
                      ?.pending_amount
                  }
                  strong
                />

              </div>


              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                <Field label="Received Amount">
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    max={
                      payment
                        .payment_summary
                        ?.pending_amount
                    }
                    value={
                      paymentForm.amount
                    }
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        amount:
                          e.target.value,
                      })
                    }
                    placeholder="Enter payment amount"
                    autoFocus
                  />
                </Field>


                <Field label="Receipt Date">
                  <Input
                    type="date"
                    value={
                      paymentForm
                        .receipt_date
                    }
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        receipt_date:
                          e.target.value,
                      })
                    }
                  />
                </Field>


                <Field label="Payment Mode">
                  <Select
                    value={
                      paymentForm
                        .payment_mode
                    }
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        payment_mode:
                          e.target.value,
                      })
                    }
                  >
                    {PAYMENT_MODES.map(
                      ([value, label]) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      )
                    )}
                  </Select>
                </Field>


                <Field label="Reference / Transaction No">
                  <Input
                    value={
                      paymentForm
                        .reference_no
                    }
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        reference_no:
                          e.target.value,
                      })
                    }
                    placeholder="UTR / cheque / reference"
                  />
                </Field>


                <div className="md:col-span-2">
                  <Field label="Notes">
                    <textarea
                      rows={3}
                      value={
                        paymentForm.notes
                      }
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          notes:
                            e.target.value,
                        })
                      }
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional payment notes"
                    />
                  </Field>
                </div>

              </div>


              <div className="mt-5 p-3 rounded-lg bg-blue-50 text-xs text-blue-800">
                After saving, the invoice status is updated automatically:
                partial payment becomes Partially Paid, full payment becomes Paid,
                and any unpaid balance after the due date becomes Overdue.
              </div>


              <div className="flex justify-end gap-2 mt-5">

                <Btn
                  variant="outline"
                  onClick={() =>
                    setPayment(null)
                  }
                >
                  Cancel
                </Btn>

                <Btn
                  onClick={
                    savePayment
                  }
                >
                  <IndianRupee
                    size={15}
                  />
                  Save Payment
                </Btn>

              </div>

            </div>
          )}

        </DialogContent>
      </Dialog>


      {/* EDIT DRAFT */}
      <Dialog
        open={!!edit}
        onOpenChange={(o) => {
          if (!o) {
            setEdit(null);
            setEditForm(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Edit Draft Invoice
            </DialogTitle>
          </DialogHeader>

          {edit && editForm && (
            <InvoiceEdit
              data={edit}
              form={editForm}
              setForm={setEditForm}
              onSave={saveEdit}
              onCancel={() => {
                setEdit(null);
                setEditForm(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceEdit({
  data,
  form,
  setForm,
  onSave,
  onCancel,
}) {
  const inv = data.invoice;

  const set = (k) => (e) =>
    setForm({
      ...form,
      [k]: e.target.value,
    });

  const earnedWages = Number(
    inv.total_earned_wages || 0
  );

  const employerPF = Number(
    inv.employer_pf || 0
  );

  const employerESI = Number(
    inv.employer_esi || 0
  );

  const serviceChargeBase = Number(
    inv.service_charge_base ||
    inv.payroll_cost ||
    0
  );

  const servicePct =
    inv.service_charge_pct ??
    inv.margin_percent ??
    0;

  const serviceCharge = Number(
    inv.service_charge ??
    inv.margin_amount ??
    0
  );

  const taxableValue = Number(
    inv.taxable_value ||
    inv.subtotal ||
    0
  );

  const gstPct = Number(
    form.gst_pct || 0
  );

  const sgstPct =
    gstPct / 2;

  const cgstPct =
    gstPct / 2;

  const sgst =
    taxableValue * sgstPct / 100;

  const cgst =
    taxableValue * cgstPct / 100;

  const grand =
    taxableValue + sgst + cgst;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Invoice Date">
          <Input
            type="date"
            value={form.invoice_date}
            onChange={set("invoice_date")}
          />
        </Field>

        <Field label="Due Date">
          <Input
            type="date"
            value={form.due_date}
            onChange={set("due_date")}
          />
        </Field>

        <Field label="Work Order / PO">
          <Input
            value={form.work_order}
            onChange={set("work_order")}
          />
        </Field>

        <Field label="Reference No">
          <Input
            value={form.reference_no}
            onChange={set("reference_no")}
          />
        </Field>

        <Field label="Payment Terms (Days)">
          <Input
            type="number"
            min="0"
            value={form.payment_terms_days}
            onChange={set("payment_terms_days")}
          />
        </Field>

        <Field label="GST %">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.gst_pct}
            onChange={set("gst_pct")}
          />
        </Field>

        <Field label="Reverse Charge">
          <Select
            value={form.reverse_charge}
            onChange={set("reverse_charge")}
          >
            <option value="NO">
              NO
            </option>

            <option value="YES">
              YES
            </option>
          </Select>
        </Field>

        <div />

        <div className="col-span-2">
          <Field label="Billing Address">
            <textarea
              value={form.billing_address}
              onChange={set("billing_address")}
              rows={3}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </Field>
        </div>

        <div className="col-span-2">
          <Field label="Subject">
            <textarea
              value={form.subject}
              onChange={set("subject")}
              rows={2}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </Field>
        </div>

        <div className="col-span-2">
          <Field label="Remarks">
            <textarea
              value={form.remarks}
              onChange={set("remarks")}
              rows={3}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </Field>
        </div>
      </div>

      <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="font-semibold text-sm">
            Invoice Calculation
          </div>

          <div className="text-xs text-slate-500 mt-1">
            Service Charge is calculated from Payroll and cannot be changed from the invoice.
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <SummaryBox
              label="Earned Wages"
              value={earnedWages}
            />

            <SummaryBox
              label="Employer PF"
              value={employerPF}
            />

            <SummaryBox
              label="Employer ESI"
              value={employerESI}
            />

            <SummaryBox
              label="Service Charge Base"
              value={serviceChargeBase}
            />

            <SummaryBox
              label={`Service Charge @ ${servicePct}%`}
              value={serviceCharge}
            />

            <SummaryBox
              label="Sub Total"
              value={taxableValue}
            />

            <SummaryBox
              label={`SGST @ ${sgstPct}%`}
              value={sgst}
            />

            <SummaryBox
              label={`CGST @ ${cgstPct}%`}
              value={cgst}
            />

            <SummaryBox
              label="Grand Total"
              value={grand}
              strong
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <Btn
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Btn>

        <Btn onClick={onSave}>
          Save Invoice
        </Btn>
      </div>
    </div>
  );
}

function SummaryBox({
  label,
  value,
  strong,
}) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="nk-overline">
        {label}
      </div>

      <div
        className={`nk-num mt-1 ${
          strong
            ? "text-lg font-bold text-blue-900"
            : "font-semibold text-slate-900"
        }`}
      >
        ₹{inr(value)}
      </div>
    </div>
  );
}

function InvoicePreview({
  invoice,
  client,
  org,
  inr,
}) {
  const lines = invoice.lines || [];

  const totalEmployees = lines.reduce(
    (sum, ln) =>
      sum + Number(ln.employees || 0),
    0
  );

  const totalShifts = lines.reduce(
    (sum, ln) =>
      sum + Number(ln.duty_shifts || 0),
    0
  );

  const totalWages = lines.reduce(
    (sum, ln) =>
      sum + Number(ln.earned_wages || 0),
    0
  );

  const servicePct =
    invoice.service_charge_pct ??
    invoice.margin_percent ??
    0;

  const companyName =
    org.display_name ||
    org.company_name ||
    "Nakshatra Housekeeping Services";

  return (
    <div
      className="bg-white border border-slate-300 rounded-md overflow-hidden"
      data-testid="invoice-preview"
    >
      {/* HEADER */}
      <div className="grid grid-cols-2 border-b border-slate-300">
        <div className="p-5">
          <Logo
            onLight
            height={48}
          />

          <div className="mt-3 text-xs leading-5 text-slate-600">
            <div className="font-bold text-slate-900 text-sm">
              {companyName}
            </div>

            <div>
              {org.address || "—"}
            </div>

            <div>
              {[org.city, org.state, org.pin]
                .filter(Boolean)
                .join(", ")}
            </div>

            {(org.phone || org.email) && (
              <div>
                {org.phone || ""}
                {org.phone && org.email ? " · " : ""}
                {org.email || ""}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 text-right flex flex-col items-end justify-start">
          <h2 className="nk-head text-2xl font-bold text-slate-900">
            TAX INVOICE
          </h2>

          <div className="mt-3 text-xs text-slate-600 space-y-1">
            <div>
              <span className="font-semibold text-slate-900">
                Invoice No:
              </span>{" "}
              {invoice.number}
            </div>

            <div>
              <span className="font-semibold text-slate-900">
                Invoice Date:
              </span>{" "}
              {invoice.invoice_date ||
                (invoice.created_at || "").slice(0, 10)}
            </div>
          </div>
        </div>
      </div>

      {/* CLIENT + INVOICE DETAILS */}
      <div className="grid grid-cols-2 border-b border-slate-300 text-xs">
        <div className="p-4 border-r border-slate-300">
          <div className="font-semibold text-slate-900 mb-2">
            To
          </div>

          <div className="font-semibold">
            {client.legal_name ||
              client.name ||
              "—"}
          </div>

          <div className="text-slate-600 leading-5 mt-1">
            {invoice.billing_address ||
              client.address ||
              "—"}

            <br />

            {[client.city, client.state]
              .filter(Boolean)
              .join(", ")}

            <br />

            GSTIN: {client.gstin || "—"}
          </div>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-[120px_1fr] gap-y-1.5">
            <div className="text-slate-500">
              PAN
            </div>
            <div className="font-medium">
              {org.pan || "—"}
            </div>

            <div className="text-slate-500">
              GSTIN
            </div>
            <div className="font-medium">
              {org.gstin || "—"}
            </div>

            <div className="text-slate-500">
              Reverse Charge
            </div>
            <div className="font-medium">
              {invoice.reverse_charge || "NO"}
            </div>

            <div className="text-slate-500">
              Billing Period
            </div>
            <div className="font-medium">
              {invoice.period_from && invoice.period_to
                ? `${invoice.period_from} to ${invoice.period_to}`
                : invoice.month}
            </div>
          </div>
        </div>
      </div>

      {/* SUBJECT / REFERENCE */}
      <div className="text-xs border-b border-slate-300">
        <div className="grid grid-cols-[130px_1fr] border-b border-slate-200">
          <div className="p-3 font-semibold bg-slate-50">
            Subject
          </div>

          <div className="p-3">
            {invoice.subject || "—"}
          </div>
        </div>

        <div className="grid grid-cols-[130px_1fr]">
          <div className="p-3 font-semibold bg-slate-50">
            Contract / WO Ref
          </div>

          <div className="p-3">
            {invoice.work_order ||
              invoice.reference_no ||
              "—"}
          </div>
        </div>
      </div>

      {/* DESIGNATION TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="bg-slate-100">
            <tr>
              <th className="p-2 border border-slate-300 text-center">
                S.No
              </th>

              <th className="p-2 border border-slate-300 text-center">
                SAC Code
              </th>

              <th className="p-2 border border-slate-300 text-left">
                Designation
              </th>

              <th className="p-2 border border-slate-300 text-right">
                No. of Employees
              </th>

              <th className="p-2 border border-slate-300 text-right">
                Rate / Day
              </th>

              <th className="p-2 border border-slate-300 text-right">
                Duty Shifts
              </th>

              <th className="p-2 border border-slate-300 text-right">
                Earned Wage Amount
              </th>
            </tr>
          </thead>

          <tbody>
            {lines.map((ln, i) => (
              <tr key={i}>
                <td className="p-2 border border-slate-300 text-center">
                  {ln.s_no ?? i + 1}
                </td>

                <td className="p-2 border border-slate-300 text-center">
                  {ln.sac_code || "—"}
                </td>

                <td className="p-2 border border-slate-300 font-medium">
                  {ln.designation || "—"}
                </td>

                <td className="p-2 border border-slate-300 text-right">
                  {ln.employees || 0}
                </td>

                <td className="p-2 border border-slate-300 text-right">
                  ₹{inr(ln.rate_per_day)}
                </td>

                <td className="p-2 border border-slate-300 text-right">
                  {Number(
                    ln.duty_shifts || 0
                  ).toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}
                </td>

                <td className="p-2 border border-slate-300 text-right">
                  ₹{inr(ln.earned_wages)}
                </td>
              </tr>
            ))}

            <tr className="bg-slate-50 font-bold">
              <td className="p-2 border border-slate-300" />

              <td className="p-2 border border-slate-300" />

              <td className="p-2 border border-slate-300">
                TOTAL
              </td>

              <td className="p-2 border border-slate-300 text-right">
                {totalEmployees}
              </td>

              <td className="p-2 border border-slate-300" />

              <td className="p-2 border border-slate-300 text-right">
                {totalShifts.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </td>

              <td className="p-2 border border-slate-300 text-right">
                ₹{inr(totalWages)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* FINANCIAL SUMMARY */}
      <div className="flex justify-end">
        <table className="text-xs w-full max-w-[520px] border-l border-slate-300">
          <tbody>
            <InvoiceTotalRow
              label="Total Earned Wages"
              value={invoice.total_earned_wages}
              inr={inr}
            />

            <InvoiceTotalRow
              label="Employer PF"
              value={invoice.employer_pf}
              inr={inr}
            />

            <InvoiceTotalRow
              label="Employer ESI"
              value={invoice.employer_esi}
              inr={inr}
            />

            <InvoiceTotalRow
              label="Total"
              value={
                invoice.service_charge_base ||
                invoice.payroll_cost
              }
              inr={inr}
              strong
            />

            <InvoiceTotalRow
              label={`Service Charge @ ${servicePct}%`}
              value={
                invoice.service_charge ??
                invoice.margin_amount
              }
              inr={inr}
            />

            <InvoiceTotalRow
              label="Sub Total"
              value={
                invoice.taxable_value ||
                invoice.subtotal
              }
              inr={inr}
              strong
            />

            <InvoiceTotalRow
              label={`SGST @ ${invoice.sgst_pct || 0}%`}
              value={invoice.sgst}
              inr={inr}
            />

            <InvoiceTotalRow
              label={`CGST @ ${invoice.cgst_pct || 0}%`}
              value={invoice.cgst}
              inr={inr}
            />

            <InvoiceTotalRow
              label="Grand Total"
              value={invoice.grand_total}
              inr={inr}
              grand
            />
          </tbody>
        </table>
      </div>

      {/* AMOUNT IN WORDS */}
      <div className="border-t border-slate-300 p-4 text-xs">
        <span className="font-semibold">
          Amount in Words:
        </span>{" "}

        <span className="text-slate-600">
          Refer PDF / Excel generated amount in words
        </span>
      </div>

      {/* BANK / SIGNATORY */}
      <div className="grid grid-cols-2 border-t border-slate-300 text-xs">
        <div className="p-4 border-r border-slate-300">
          <div className="font-semibold mb-2">
            Bank Details
          </div>

          <div className="text-slate-600 leading-5">
            Bank: {org.bank_name || "—"}
            <br />
            A/C Name: {org.account_name || "—"}
            <br />
            A/C No: {org.account_number || "—"}
            <br />
            IFSC: {org.ifsc || "—"}
            <br />
            Branch: {org.branch || "—"}
            <br />
            EPF Estt: {org.epf_number || "—"}
            <br />
            ESIC Code: {org.esic_code || "—"}
          </div>
        </div>

        <div className="p-4 text-right min-h-[130px] flex flex-col justify-between">
          <div className="font-semibold">
            For {companyName}
          </div>

          <div>
            {org.signature && (
              <img
                src={org.signature}
                alt="signature"
                className="ml-auto mb-2 max-h-12 object-contain"
              />
            )}

            <div className="font-semibold">
              {org.signatory_name ||
                "Authorised Signatory"}
            </div>

            {org.signatory_designation && (
              <div className="text-slate-500">
                {org.signatory_designation}
              </div>
            )}
          </div>
        </div>
      </div>

      {invoice.remarks && (
        <div className="border-t border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
          <b>Remarks:</b>{" "}
          {invoice.remarks}
        </div>
      )}
    </div>
  );
}

function InvoiceTotalRow({
  label,
  value,
  inr,
  strong = false,
  grand = false,
}) {
  return (
    <tr
      className={
        grand
          ? "bg-slate-100 font-bold text-slate-900"
          : strong
          ? "font-semibold text-slate-900"
          : ""
      }
    >
      <td className="p-2.5 border-b border-r border-slate-300">
        {label}
      </td>

      <td className="p-2.5 border-b border-slate-300 text-right nk-num min-w-[150px]">
        ₹{inr(value)}
      </td>
    </tr>
  );
}
